import { addDays, isAfter } from 'date-fns';
import {
	Quote,
	QuoteProduct,
	DecisionTree,
	QuoteStatus,
	DecisionTreeOrQuoteProduct,
	Product,
	QuotePackage,
	Package,
} from '../shared/types/quotes';
import { StatusNumber } from './statusNumber';
import * as Yup from 'yup';
import { ReferralReason } from './referralReason';

// Constants for policy period configuration
export const MIN_POLICY_PERIOD = 9;
export const MAX_POLICY_PERIOD = 16;
export const MAX_FORWARD_DAYS = 45;

export const MIN_DATE = new Date();
export const MAX_DATE = addDays(MIN_DATE, MAX_FORWARD_DAYS);
MAX_DATE.setHours(23, 59, 59, 999); // Set MAX_DATE to very end of the day

export const regexGSOF = /\{GSOF\}/;

export const passwordValidation = Yup.string()
	.min(16, 'Password must be at least 16 characters')
	.matches(
		/^[a-zA-Z0-9@$!%*#?&-_.,]+$/,
		'Password can only have letters, digits or @$!%*#?&-_., characters'
	)
	.matches(/[a-z]+/, 'Password must have at least one lowercase character')
	.matches(/[A-Z]+/, 'Password must have at least one uppercase character')
	.matches(/\d+/, 'Password must have at least one digit')
	.matches(/[@$!%*#?&-_.,]+/, 'Password must have at least one non-alphanumeric character')
	.test('unique-characters', 'Password must have at least 10 unique characters', (value) => {
		const uniqueChars = new Set(value);
		return uniqueChars.size >= 10;
	})
	.required('Please enter a password');

export const GENERIC_ERROR = 'Please try again and contact Delta if issue persists';

/**
 * Function to format a number as currency in AUD (Australian Dollars) with a narrow currency symbol.
 * @param {number} num - The number to format as currency.
 * @returns {string} A formatted currency string.
 */
export const formatCurrency = (num: number) => {
	// Use the Intl.NumberFormat object to format the 'num' as currency.
	return Intl.NumberFormat('en-US', {
		style: 'currency', // Format as currency.
		currency: 'AUD', // Use AUD (Australian Dollars).
		currencyDisplay: 'narrowSymbol', // Display the currency symbol (AUD) in a narrow format.
	}).format(num);
};

export const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

/**
 * Function to find the difference between two arrays 'a' and 'b'.
 * @param {any[]} a - The first array.
 * @param {any[]} b - The second array.
 * @returns {any[]} An array containing elements that are unique to either 'a' or 'b'.
 */
export const difference = (a: any[], b: any[]) => {
	// Concatenate arrays 'a' and 'b', then filter to include only elements that are unique
	return a.concat(b).filter((val, index, arr) => arr.indexOf(val) === arr.lastIndexOf(val));
};

export const checkQuestionErrors = (question: any): boolean => {
	let additionalQuestion = question.decisionTreeOptions.find(
		(option: any) =>
			option.selected &&
			option.decisionTreeOptionAction.name.trim().toUpperCase() === 'ADDITIONAL QUESTION'
	);
	// console.log("Additional question", additionalQuestion);
	return (
		question.decisionTreeOptions.every((o: any) => !o.selected) ||
		(additionalQuestion != null &&
			checkQuestionErrors(additionalQuestion.decisionTreeOptionQuestion))
	);
};

export const hrStyle = {
	color: '#CCDB67',
	backgroundColor: '#CCDB67',
	borderColor: '#CCDB67',
	height: 5,
	padding: 0,
	border: 0,
	marginLeft: -16,
	marginRight: -16,
};

export const findQuoteStatusByProductId = (
	quoteGroup: Quote[],
	productId: number
): QuoteStatus | undefined => {
	for (const quote of quoteGroup) {
		const product = quote.quoteProducts?.some((p) => p.id === productId);
		if (product) {
			return quote.status;
		}

		const pack = quote.quotePackages?.find((q) =>
			q.quoteProducts?.some((p) => p.id === productId)
		);
		if (pack) {
			return quote.status;
		}
	}

	return undefined;
};

const getAdditionalQuestions = (currentTab: any) => {
	let tempAdditionalQuestions: any[] = [];
	if (currentTab && currentTab.decisionTreeQuestions) {
		tempAdditionalQuestions = currentTab.decisionTreeQuestions
			.filter((q: any) => {
				let option = q.decisionTreeOptions.find((o: any) => o.selected);
				return (
					option != null &&
					option.decisionTreeOptionAction.name.toUpperCase() === 'ADDITIONAL QUESTION'
				);
			})
			.map(
				(q: any) =>
					q.decisionTreeOptions.find((o: any) => o.selected).decisionTreeOptionQuestion
			);
	}
	return tempAdditionalQuestions;
};

export const getReferralQuestions = (currentTab: any) => {
	let tempReferralQuestions: any[] = [];

	if (currentTab && currentTab.decisionTreeQuestions) {
		tempReferralQuestions = [
			...currentTab.decisionTreeQuestions.filter((q: any) => {
				let option = q.decisionTreeOptions.find((o: any) => o.selected);
				return (
					option != null && option.decisionTreeOptionAction.name.toUpperCase() === 'REFER'
				);
			}),
		].flat(); //.map((q: any) => {return {...q, productName: currentTab.name}});

		const tempAdditionalQuestions: any[] = getAdditionalQuestions(currentTab);
		// CHECK ADDITIONAL QUESTIONS FOR REFERRAL STATUS
		tempReferralQuestions = [
			...tempReferralQuestions,
			tempAdditionalQuestions.filter((q: any) => {
				let option = q.decisionTreeOptions.find((o: any) => o.selected);
				return (
					option != null &&
					option?.decisionTreeOptionAction?.name.toUpperCase() === 'REFER'
				);
			}),
		].flat();
	}
	return tempReferralQuestions;
};

export const getDeclinedQuestions = (currentTab: any) => {
	let tempDeclineQuestions: any[] = [];

	if (currentTab && currentTab.decisionTreeQuestions) {
		tempDeclineQuestions = [
			...currentTab.decisionTreeQuestions.filter((q: any) => {
				let option = q.decisionTreeOptions.find((o: any) => o.selected);
				return (
					option != null &&
					option.decisionTreeOptionAction.name.toUpperCase() === 'DECLINE'
				);
			}),
		].flat(); //.map((q: any) => {return {...q, productName: currentTab.name}});

		const tempAdditionalQuestions: any[] = getAdditionalQuestions(currentTab);
		// CHECK ADDITIONAL QUESTIONS FOR DECLINE STATUS
		tempDeclineQuestions = [
			...tempDeclineQuestions,
			tempAdditionalQuestions.filter((q: any) => {
				let option = q.decisionTreeOptions.find((o: any) => o.selected);
				return (
					option != null &&
					option.decisionTreeOptionAction.name.toUpperCase() === 'DECLINE'
				);
			}),
		].flat();
	}
	// console.log(tempDeclineQuestions);
	return tempDeclineQuestions;
};

export const getProductValues = (
	item: Product,
	currentQuotes: Quote[],
	decisionTree: DecisionTreeOrQuoteProduct[]
) => {
	const products = currentQuotes
		.flatMap((q) => q.quoteProducts)
		.concat(currentQuotes.flatMap((q) => q.quotePackages.flatMap((p) => p.quoteProducts)));
	const quoteItem = products.find((p) => p.id == item.id && p.name == item.name);

	let liability: any =
		quoteItem?.quoteProductSections?.[0]?.liability ?? item.productSections[0].liability;
	let excess: any =
		quoteItem?.quoteProductSections?.[0]?.excess ?? item.productSections[0].excess;
	//liability = Array.isArray(liability) ? liability[0] : liability;
	let premium: any = quoteItem?.quoteProductSections?.[0]?.totals?.basePremium;

	// let liabilityValue;

	// Check if the product id is Product Recall and the liability array has 2 or more elements
	if ((item.id === 109 || item.id === 110) && Array.isArray(liability) && liability.length >= 2) {
		// Select the second element of the liability array
		liability = liability[1];
	} else {
		// Default behavior for other cases
		liability = Array.isArray(liability) ? liability[0] : liability;
	}
	//----------

	// liability = liabilityValue;

	excess = Array.isArray(excess) ? excess[0] : excess;

	const status = findQuoteStatusByProductId(currentQuotes, item.id);
	if (
		status &&
		(status.statusNumber === StatusNumber.Declined ||
			status.statusNumber === StatusNumber.Referred)
	) {
		premium = status.name;
	}

	let referralQuestions: any[] = [];
	let declineQuestions: any[] = [];

	const itemQuestions: any =
		decisionTree.find(
			(r: any) =>
				r.quoteProductDecisionTree?.productId ===
				quoteItem?.quoteProductDecisionTree?.productId
		) ?? {};

	// if (itemQuestions && "quoteProductDecisionTree" in itemQuestions) {
	// 	referralQuestions = getReferralQuestions(itemQuestions.quoteProductDecisionTree);
	// 	// console.log(`Referral questions ${item.name} ${item.id}`, referralQuestions);
	// 	if (referralQuestions.length > 0) {
	// 		premium = "Referral required";
	// 	}
	// 	declineQuestions = getDeclinedQuestions(itemQuestions.quoteProductDecisionTree);
	// 	if (declineQuestions.length > 0) {
	// 		premium = "Declined";
	// 	}
	// }

	if (!premium) {
		premium = '-';
	}

	return { excess, liability, premium, referralQuestions, declineQuestions, status };
};

export const isDateDisabled = (date: Date | null, max?: Date | null, min?: Date | null) => {
	// console.log(
	// 	date,
	// 	'MAX DATE',
	// 	MAX_DATE,
	// 	'Is date after MAX DATE?',
	// 	date && isAfter(date, max ?? MAX_DATE),
	// 	'MIN DATE',
	// 	MIN_DATE,
	// 	'Is min date before MIN DATE?',
	// 	date && isAfter(MIN_DATE, min ?? date)
	// );
	return !date || isAfter(date, max ?? MAX_DATE) || isAfter(MIN_DATE, min ?? date);
};

export const convertQuoteProductToProduct = (item: QuoteProduct): Product => {
	return {
		id: item.id,
		name: item.name,
		isMainForPackage: item.isMainForPackage ?? true,
		productSections: item.quoteProductSections.map((section) => ({
			excess: [section.excess],
			id: section.id,
			name: section.productSectionName,
			liability: [section.liability],
		})),
	};
};

export const convertQuotePackageToPackage = (item: QuotePackage): Package => {
	return {
		id: item.id,
		name: item.name,
		products: item.quoteProducts.map((i) => convertQuoteProductToProduct(i)),
	};
};

export const checkIfProductHasReferralReason = (product: QuoteProduct) =>
	(product.referralReason && product.referralReason !== ReferralReason.ReferralNotNeeded) || product.referralReasons.length > 0;

export const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

export const setDeepValue = (obj: any, path: string, value: any) => {
    const parts = path
        .replace(/\[(\w+)\]/g, '.$1')
        .split('.')
        .filter(Boolean);

    const newObj = structuredClone(obj);
    let pointer = newObj;

    for (let i = 0; i < parts.length; i++) {
        const key = parts[i];

        const isLast = i === parts.length - 1;

        if (isLast) {
            pointer[key] = value;
            break;
        }

        const nextKey = parts[i + 1];
        const shouldBeArray = /^\d+$/.test(nextKey); // if next key is number → array

        if (pointer[key] === undefined) {
            pointer[key] = shouldBeArray ? [] : {};
        } else {
            pointer[key] = Array.isArray(pointer[key]) ? [...pointer[key]] : { ...pointer[key] };
        }

        pointer = pointer[key];
    }

    return newObj;
};